import { HttpPatchUtilityService } from './../../utility/http-patch-utility/http-patch-utility.service';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpEvent, HttpResponse, HttpEventType, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApplicationDocuments } from '../../models/application/application-documents.interface';
import { ApplicationDocumentType } from '../../models/application/application-document-type.interface';
import { ApplicationDocumentsPatch } from '../../models/patch/application-documents.patch.interface';
import { ApplicationDocumentMetadata } from '../../models/application/application-document-metadata.interface';
import { DocumentFeature } from '../../models/program/document-feature.interface';
import { constants, DOCUMENT_SOURCE_CODES } from '@edassist/common';
import { DocumentOcrValidationResult } from '../../models/application/application-document-ocr-validated';
import { DocumentDefaultFeature } from '../../models/program/document-default-feature.interface';
import { HttpParamObject } from '../../utility/http-utility/http-param-object.interface';
import { HttpUtilityService } from '../../utility/http-utility/http-utility.service';

@Injectable({ providedIn: 'root' })
export class ApplicationDocumentsService {
	private readonly relativeUrl = 'applications/';

	constructor(private readonly http: HttpClient) {}

	getApplicationDocuments(applicationId: number): Observable<Array<ApplicationDocuments>> {
		return this.http.get<Array<ApplicationDocuments>>(`${this.relativeUrl}${applicationId}/application-documents`);
	}

	getApplicationDocument(applicationId: number, documentId: number): Observable<ApplicationDocuments> {
		return this.http.get<ApplicationDocuments>(
			`${this.relativeUrl}${applicationId}/application-documents/${documentId}/document-info`
		);
	}

	getAllApplicationDocumentsZip(
		applicationId: number,
		documentSourceCode?: DOCUMENT_SOURCE_CODES
	): Observable<HttpResponse<Blob>> {
		let params = new HttpParams();
		if (documentSourceCode) {
			params = params.set('documentSourceCode', documentSourceCode);
		}

		return this.http.get(`${this.relativeUrl}${applicationId}/application-documents/download-all`, {
			params,
			observe: 'response',
			responseType: 'blob'
		});
	}

	getApplicationDocumentsOfSourceCode(
		applicationId: number,
		documentSourceCode: DOCUMENT_SOURCE_CODES
	): Observable<Array<ApplicationDocuments>> {
		const queryParams: HttpParamObject = HttpUtilityService.toPlainHttpParams({
			documentSourceCode
		});

		return this.http.get<Array<ApplicationDocuments>>(
			`${this.relativeUrl}${applicationId}/application-documents`,
			queryParams
		);
	}

	uploadDocument(
		applicationId: number,
		fileData: File,
		fileMetadata: ApplicationDocumentMetadata,
		isLoanApplication: boolean
	): Observable<HttpEvent<void>> {
		const formData = new FormData();
		formData.append('file', fileData);
		formData.append('dmsLocation', 'queued');
		formData.append('isQueued', 'true');
		formData.append('applicationComment', fileMetadata.applicationComment);
		formData.append('documentType', fileMetadata.documentType);
		formData.append('visibleToParticipant', JSON.stringify(fileMetadata.visibleToParticipant));
		if (!isLoanApplication) {
			formData.append('visibleToClientAdmin', JSON.stringify(fileMetadata.visibleToClientAdmin));
		}
		if (fileMetadata.documentType === constants.applicationUploadDocumentTypes.grades) {
			formData.append('gradeId', fileMetadata.gradeId.toString());
			formData.append('courseId', fileMetadata.courseId.toString());
		}

		return this.http.post<void>(`${this.relativeUrl}${applicationId.toString()}/application-documents/`, formData, {
			reportProgress: true,
			observe: 'events'
		});
	}

	patchApplicationDocument(
		applicationId: number,
		document: ApplicationDocuments,
		updates: ApplicationDocumentsPatch
	): Observable<ApplicationDocuments> {
		const patchOperation = HttpPatchUtilityService.convertDataToHttpPatchOperation(document, {
			...document,
			...updates
		});

		return this.http.patch<ApplicationDocuments>(
			`${this.relativeUrl}${applicationId.toString()}/application-documents/${document.id}`,
			patchOperation
		);
	}

	getEligibleDocumentTypes(applicationId: number): Observable<Array<ApplicationDocumentType>> {
		return this.http.get<Array<ApplicationDocumentType>>(
			`${this.relativeUrl}${applicationId.toString()}/application-eligible-document-types`
		);
	}

	serveApplicationDocument(applicationId: number, document: ApplicationDocuments): Observable<any> {
		return this.http.get<any>(
			`${this.relativeUrl}${applicationId.toString()}/application-documents/${document.id.toString()}`,
			{
				responseType: 'blob' as 'json',
				observe: 'response'
			}
		);
	}

	serveApplicationDocumentById(applicationId: number, documentId: number): Observable<HttpResponse<Blob>> {
		return this.http.get<any>(
			`${this.relativeUrl}${applicationId.toString()}/application-documents/${documentId.toString()}`,
			{
				responseType: 'blob' as 'json',
				observe: 'response'
			}
		);
	}

	generateApplicationLetterOfCredit(applicationId: number): Observable<HttpResponse<Blob>> {
		const headers = new HttpHeaders({ 'Content-Type': 'application/pdf' });

		return this.http.post<any>(
			`${this.relativeUrl}${applicationId.toString()}/generate-letter-of-credit`,
			{},
			{
				headers,
				responseType: 'blob' as 'json',
				observe: 'response'
			}
		);
	}

	generateVerificationLetter(applicationId: number): Observable<HttpResponse<Blob>> {
		const headers = new HttpHeaders({ 'Content-Type': 'application/pdf' });

		return this.http.post<any>(
			`${this.relativeUrl}${applicationId.toString()}/generate-verification-letter`,
			{},
			{
				headers,
				responseType: 'blob' as 'json',
				observe: 'response'
			}
		);
	}

	getProgramDocumentFeature(programId: number): Observable<DocumentFeature> {
		return this.http.get<DocumentFeature>(`programs/${programId.toString()}/features/document`);
	}

	getOcrValidatedDocument(applicationId: number, documentId: number): Observable<DocumentOcrValidationResult> {
		return this.http.get<DocumentOcrValidationResult>(
			`document-validation/applications/${applicationId.toString()}/documents/${documentId.toString()}`,
			{}
		);
	}

	getDefaultDocuments(programId: number): Observable<Array<DocumentDefaultFeature>> {
		return this.http.get<Array<DocumentDefaultFeature>>(
			`programs/${programId.toString()}/features/document/default-documents`
		);
	}

	deleteApplicationDocument(applicationId: number, documentId: number): Observable<void> {
		return this.http.delete<void>(
			`applications/${applicationId.toString()}/application-documents/${documentId.toString()}`
		);
	}

	processUploadEvent(uploadEvent: HttpEvent<void>): {
		uploadEvent: HttpEvent<void>;
		isUploading: boolean;
		uploadProgress: number;
	} {
		let isUploading = true;
		let uploadProgress = 0;

		switch (uploadEvent.type) {
			case HttpEventType.UploadProgress:
				isUploading = true;
				uploadProgress = Math.round((uploadEvent.loaded * 100) / uploadEvent.total);
				break;

			case HttpEventType.Response:
				isUploading = false;
				uploadProgress = 0;
				break;

			default:
				break;
		}

		return { uploadEvent, isUploading, uploadProgress };
	}
}
